import React from "react";
import {
  hospitalBed,
  microscope,
  patientIcon,
  pills,
  stethocope,
} from "../Assets/Icons";
import { delman, master, monitoringDocs, mujkanovic } from "../Assets/Images";
import ImageCard from "../Components/ImageCard";
import Card from "../Components/Card";
import Button from "../Components/Button";

export const WELCOME_PAGE_CONTENT = {
  HERO_TITLE: "The Crotts Global Initiative For Extramammary Paget’s Disease",
  HERO_DESCRIPTION:
    "The comprehensive global resource to optimize EMPD Treatment, Outcomes, Education, and Innovation.",
  WHAT_IS_EMPD: {
    ID: "what-is-empd",
    TITLE: "What is EMPD?",
    DESCRIPTION:
      "Extramammary Paget’s Disease (EMPD) is a rare skin condition that is often found near the genitals, most commonly on the vulva. It can also be on the skin around the anus, penis, scrotum, perineum and armpits. EMPD usually causes a red, scaly or crusty area on the skin that is itchy and grows slowly.",
    CARDS: [
      {
        icon: patientIcon,
        header: "Rare Disease",
        description:
          "EMPD is a rare form of skin cancer known as cutaneous malignant disease that primarily affects areas where there are many sweat glands that open into hair follicles (apocrine sweat glands).",
      },
      {
        icon: hospitalBed,
        header: "Very Few Cases",
        description:
          "While relatively few detailed case reports of Extramammary Paget’s Disease (EMPD) have been published, population-based data indicate that several thousand EMPD cases occur worldwide each year.",
      },
      {
        icon: stethocope,
        header: "Unseen by Physicians",
        description:
          "As most physicians have never seen a case of EMPD, early diagnosis is frequently delayed, which can lead to the spread of disease from skin to lymph nodes or distant organs.",
      },
    ],
  },
  CHALLENGES: {
    ID: "empd-challenges",
    LEFT_CONTENT: <img alt="doctors-on-computer" src={monitoringDocs} />,
    RIGHT_CONTENT: (
      <React.Fragment>
        <h2>EMPD Challenges</h2>
        <h3>Funding</h3>
        <p>
          Due to EMPD rarity, there is virtually no directed funding anywhere in
          the world that motivates researchers and clinicians to focus on the
          disease. This has caused a significant knowledge gap in developing
          effective treatments and in clinical care.
        </p>
        <h3>Treatment + Recovery</h3>
        <p>
          Current treatments are mostly surgical, often with significant
          challenges in recovery for patients. EMPD can recur and often does. No
          effective systemic therapy options exists if EMPD spreads from skin to
          lymph nodes or distant organs.
        </p>
        <h3>Lack of Data</h3>
        <p>
          Unlike for more common cancers, there is no EMPD data repository or
          dedicated international tumor bank anywhere in the world for
          researchers to access.
        </p>
      </React.Fragment>
    ),
  },
  OUR_VISION: {
    ID: "our-vision",
    TITLE: "Our Vision",
    DESCRIPTION: "The Crotts Global Initiative For Extramammary Paget's Disease",
    CARDS: [
      {
        icon: pills,
        header: "Expand Treatment Options",
        description:
          "To expand treatment options for Extramammary Paget’s Disease (EMPD) and ultimately find a cure.",
      },
      {
        icon: microscope,
        header: "Optimize Research",
        description:
          "To serve as a coordinating center and global resource to optimize EMPD science — from research bench to patient bedside.",
      },
    ],
  },
};

export const LEADERSHIP_PAGE_CONTENT = {
  HERO_TITLE: "Who We Are",
  HERO_DESCRIPTION:
    "The Crotts Global Initiative For Extramammary Paget's Disease is led by physician-researchers at Winship Cancer Institute of Emory University.",
  WHO_WE_ARE: {
    ID: "who-we-are",
    HEADER: "Our Leadership",
    LEFT_CONTENT: (
      <React.Fragment>
        <ImageCard
          image={delman}
          header="Keith Delman, MD, FSSO, FACS"
          description="Chief of Surgery, Grady Memorial Hospital, Associate Chair and Professor, Department of Surgery, Emory University School of Medicine, Member of the Discovery and Developmental Therapeutics Research Program, Winship Cancer Institute of Emory University "
          link="https://winshipcancer.emory.edu/profiles/delman-keith.php"
          linkLabel="View Profile"
        />
        <ImageCard
          image={master}
          header="Viraj Master, MD, PhD, FACS"
          description="Executive Director, Emory Specialized Patient Services, Emory Healthcare, Professor, Fray Marshall Chair of Urological Research, Director of Clinical Research Unit, Department of Urology, Director of Integrative Oncology and Survivorship, Winship Cancer Institute, Eminent Physician, School of Medicine, Emory University"
          link="https://winshipcancer.emory.edu/profiles/master-viraj.php"
          linkLabel="View Profile"
        />
        <ImageCard
          image={mujkanovic}
          header="Emina Mujkanovic"
          description="Program Coordinator, Sr., The Crotts Global Initiative for Extramammary Paget's Disease, Winship Cancer Institute, Emory University "
        />
      </React.Fragment>
    ),
    RIGHT_CONTENT: (
      <Card>
        <h2>About Our Leaders</h2>
        <p>
          Dr. Delman and Dr. Master are among the limited number of global
          experts in Extramammary Paget’s disease and share a passion for making
          a difference for patients who face this rare cancer. Dr. Delman is an
          internationally recognized expert in treating skin cancer (cutaneous
          malignancies).
        </p>
        <p>
          Dr. Master is the Chair of the Extramammary Paget's disease section of
          the Global Society of Rare Genito-urinary Tumors.{" "}
        </p>
        <p>
          At Emory, Dr. Delman and Dr. Master have a history of collaboration on
          EMPD cases and have published one of the largest series of outcomes
          for patients with Extramammary Paget's disease.
        </p>
      </Card>
    ),
  },
  CONTACT: {
      ID: "contact",
      HEADER: "Contact The Team",
      LEFT_CONTENT: (
          <Card
            blank
          >
            <h2>Information</h2>
            <p>
                Please click the button below to send an email to the EMPD team at globalempd@emory.edu
            </p>
            <p>
                <Button
                    onClick={() => {
                        window.location.href = 'mailto:globalempd@emory.edu'
                    }}
                    label={'Email the Team'}
                    className={{    
                        alignSelf: "flex-start",
                        marginTop: "20px",
                        display: "inline-block",
                    }}
                />
            </p>
		  	<br/>
		  	<hr/>
		  	<h2>Winship Cancer Institute (Clifton campus)</h2>
		  	<h4>Mailing Address for The Crotts Global Initiative For Extramammary Paget’s Disease</h4>
		  	<p>
		  		The Crotts Global Initiative For Extramammary Paget’s Disease<br/>
		  		1365 Clifton Road NE (W60)<br/>
				Atlanta, Georgia 30322<br/>
		  	</p>
          </Card>
      ),
  },
};

export const INITIATIVE_PAGE_CONTENT = {
  HERO_TITLE: "Behind The Crotts Global Initiative For Extramammary Paget's Disease",
  HERO_DESCRIPTION: "Learn more about our Global Initiative.",
  TRANSFORMATIVE: {
    ID: "tranformative",
  },
  GLOBAL: {
    ID: "global",
  },
  SUPPORT: {
    ID: "supportive",
  },
};

export const PROVIDER_REGISTRATION = {
  HERO_TITLE: "The Crotts Global Initiative For Extramammary Paget’s Disease",
  HERO_DESCRIPTION:
    "Welcome, and thank you for your interest in supporting The Crotts Global Initiative For Extramammary Paget’s Disease as a provider.  When you click the Sign Up button, please navigate to the lower right of the dialog and click \"Sign up now\" to begin the registration process."
}
export const FOOTER_COPY = `Copyright © Winship Cancer Institute of Emory University ${new Date().getFullYear()}`;
